import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailLink } from 'firebase/auth';
import { getFirestoreInstance } from '../utils/firebaseHelper';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  updateDoc,
  arrayUnion,
  arrayRemove,
  onSnapshot
} from 'firebase/firestore';
import { getUserData, updateUserProfile } from '../utils/actions/userActions';
import queryString from 'query-string';
import { checkAndUpdateMembershipStatus } from '../utils/membershipUtils';

export const UserContext = createContext();

// Cache management
const CACHE_PREFIX = 'userDataCache_';
const BAND_CACHE_PREFIX = 'bandDataCache_';
const ARTIST_CACHE_PREFIX = 'artistDataCache_';
const RECOMMENDATION_CACHE_PREFIX = 'recommendationCache_';
const EVENT_CACHE_PREFIX = 'eventDataCache_'; // New prefix for events (tours/shows)

const getUserSpecificCacheKey = (userId, baseKey) => {
  return `${baseKey}${userId}`;
};

// Update cache utilities to include userId
const saveToCache = (key, data, prefix = CACHE_PREFIX, userId = null) => {
  const cacheKey = userId ? getUserSpecificCacheKey(userId, `${prefix}${key}_`) : `${prefix}${key}`;
  const cacheData = {
    data,
    timestamp: Date.now(),
    userId: userId // Store which user this cache belongs to
  };
  localStorage.setItem(cacheKey, JSON.stringify(cacheData));
};

const getFromCache = (key, prefix = CACHE_PREFIX, userId = null) => {
  const cacheKey = userId ? getUserSpecificCacheKey(userId, `${prefix}${key}_`) : `${prefix}${key}`;
  const cached = localStorage.getItem(cacheKey);
  if (!cached) return null;
  
  try {
    const parsedCache = JSON.parse(cached);
    // Verify the cache belongs to the current user
    if (userId && parsedCache.userId && parsedCache.userId !== userId) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return parsedCache.data;
  } catch (error) {
    localStorage.removeItem(cacheKey);
    return null;
  }
};

// Update clearCache to be user-specific
const clearCache = (key = null, prefix = null, userId = null) => {
  if (key && prefix && userId) {
    const cacheKey = getUserSpecificCacheKey(userId, `${prefix}${key}_`);
    localStorage.removeItem(cacheKey);
  } else if (key && userId) {
    // Clear all cache entries for this user and key
    Object.keys(localStorage)
      .filter(storageKey => 
        storageKey.includes(getUserSpecificCacheKey(userId, `${CACHE_PREFIX}${key}_`)) ||
        storageKey.includes(getUserSpecificCacheKey(userId, `${BAND_CACHE_PREFIX}${key}_`)) ||
        storageKey.includes(getUserSpecificCacheKey(userId, `${ARTIST_CACHE_PREFIX}${key}_`)) ||
        storageKey.includes(getUserSpecificCacheKey(userId, `${EVENT_CACHE_PREFIX}${key}_`))
      )
      .forEach(storageKey => localStorage.removeItem(storageKey));
  } else {
    // Clear all cache entries (global clear)
    Object.keys(localStorage)
      .filter(key => 
        key.startsWith(CACHE_PREFIX) || 
        key.startsWith(BAND_CACHE_PREFIX) ||
        key.startsWith(ARTIST_CACHE_PREFIX) ||
        key.startsWith(RECOMMENDATION_CACHE_PREFIX) ||
        key.startsWith(EVENT_CACHE_PREFIX)
      )
      .forEach(key => localStorage.removeItem(key));
  }
};


export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Store enriched band data in context for reuse throughout the app
  const [enrichedBandData, setEnrichedBandData] = useState({});
  
  // New state for enriched event data (tours/shows)
  const [enrichedEventData, setEnrichedEventData] = useState({});
  
  // Track active listeners
  const userListener = useRef(null);
  const bandListeners = useRef({});
  const eventListeners = useRef({}); // New ref for event listeners

  // Add this to UserContext.js within the UserProvider component
  useEffect(() => {
    if (userData?.userId) {
      const db = getFirestoreInstance();
      const userDocRef = doc(db, 'users', userData.userId);
      
      const unsubscribe = onSnapshot(userDocRef, async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const firestoreData = docSnapshot.data();
          
           // Process the associatedActs array to prevent duplicates and enrich data
           let updatedAssociatedActs = firestoreData.associatedActs || [];
          
           // Always enrich the data before updating state
           if (updatedAssociatedActs?.length) {
             updatedAssociatedActs = await enrichActs(updatedAssociatedActs, 'associatedActs');
           }
          
          const newUserData = {
            ...firestoreData,
            userId: userData.userId,
            associatedActs: updatedAssociatedActs
          };
          
          // Check membership status with the new data
          const updatedData = await checkAndUpdateMembershipStatus(userData.userId, newUserData);
          
          // Update state with checked/updated data
          setUserData(prevData => ({
            ...prevData,
            ...updatedData
          }));
          
          // Update localStorage cache
          saveToCache(userData.userId, updatedData);
        }
      });
      
      return () => unsubscribe();
    }
  }, [userData?.userId]);

  /**
   * Get event data (tour or show) with caching and listeners
   */
  const getEventDataFromCache = async (eventId, eventType, forceRefresh = false) => {
    const cacheKey = `${eventType}_${eventId}`;
    
    // First check React state if we have it in memory
    if (!forceRefresh && enrichedEventData && enrichedEventData[cacheKey]) {
      return enrichedEventData[cacheKey];
    }
    
    // Then check localStorage cache
    if (!forceRefresh) {
      const cachedData = getFromCache(cacheKey, EVENT_CACHE_PREFIX);
      if (cachedData) {
        // Update state with cached data
        setEnrichedEventData(prev => ({
          ...prev,
          [cacheKey]: cachedData
        }));
        return cachedData;
      }
    }
    
    try {
      // Fetch from Firestore
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Get the main event document
      const eventDoc = await getDoc(doc(db, collectionName, eventId));
      if (!eventDoc.exists()) return null;
      
      const mainEventData = eventDoc.data();
      
      // Get event dates from eventDates subcollection
      const datesSnapshot = await getDocs(
        collection(db, collectionName, eventId, 'eventDates')
      );
      
      const eventDates = datesSnapshot.docs.map(dateDoc => ({
        id: dateDoc.id,
        ...dateDoc.data(),
      }));
      
      // Get assignedUsers from assignedUsers subcollection
      const assignedUsersSnapshot = await getDocs(
        collection(db, collectionName, eventId, 'assignedUsers')
      );
      
      const assignedUsers = assignedUsersSnapshot.docs.map(userDoc => ({
        userId: userDoc.id,
        ...userDoc.data(),
      }));
      
      // Get pendingInvitations from pendingInvitations subcollection
      const pendingInvitationsSnapshot = await getDocs(
        collection(db, collectionName, eventId, 'pendingInvitations')
      );
      
      const pendingInvitations = pendingInvitationsSnapshot.docs.map(invDoc => ({
        id: invDoc.id,
        ...invDoc.data(),
      }));
      
      const eventData = {
        id: eventId,
        type: eventType,
        ...mainEventData,
        eventDates: eventDates,
        // Keep old field for backward compatibility
        tourDates: eventDates,
        // Add new fields from subcollections
        assignedUsers: assignedUsers,
        pendingInvitations: pendingInvitations
      };
      
      // Save to cache
      saveToCache(cacheKey, eventData, EVENT_CACHE_PREFIX);
      
      // Update state
      setEnrichedEventData(prev => ({
        ...prev,
        [cacheKey]: eventData
      }));
      
      // Set up listener if not already listening
      if (!eventListeners.current[cacheKey]) {
        eventListeners.current[cacheKey] = onSnapshot(
          doc(db, collectionName, eventId),
          (snapshot) => {
            if (snapshot.exists()) {
              const updatedData = {
                id: eventId,
                type: eventType,
                ...snapshot.data(),
                eventDates: eventData.eventDates, // Keep the dates
                assignedUsers: eventData.assignedUsers, // Keep the assigned users
                pendingInvitations: eventData.pendingInvitations // Keep the pending invitations
              };
              
              // Update cache
              saveToCache(cacheKey, updatedData, EVENT_CACHE_PREFIX);
              
              // Update state
              setEnrichedEventData(prev => ({
                ...prev,
                [cacheKey]: updatedData
              }));
            }
          },
          error => {
            console.error(`Error in event listener for ${cacheKey}:`, error);
          }
        );
      }
      
      return eventData;
    } catch (error) {
      console.error(`Error fetching ${eventType} data:`, error);
      return null;
    }
  };

  /**
   * Get band data with smart caching and listeners
   */
  const getBandDataFromCache = async (bandId, forceRefresh = false) => {
    // First check React state
    if (!forceRefresh && enrichedBandData[bandId]) {
      return enrichedBandData[bandId];
    }
    
    // Then check localStorage cache
    if (!forceRefresh) {
      const cachedData = getFromCache(bandId, BAND_CACHE_PREFIX);
      if (cachedData) {
        // Update React state with cached data
        setEnrichedBandData(prev => ({
          ...prev,
          [bandId]: cachedData
        }));
        return cachedData;
      }
    }
  
    try {
      // Fetch from Firestore
      const db = getFirestoreInstance();
      const bandDoc = await getDoc(doc(db, 'bands', bandId));
      if (!bandDoc.exists()) return null;
  
      const data = {
        id: bandId,
        ...bandDoc.data()
      };
      
      // Save to both caches
      saveToCache(bandId, data, BAND_CACHE_PREFIX);
      
      setEnrichedBandData(prev => ({
        ...prev,
        [bandId]: data
      }));
      
      // Set up listener for this band if not already listening
      if (!bandListeners.current[bandId]) {
        bandListeners.current[bandId] = onSnapshot(
          doc(db, 'bands', bandId),
          (snapshot) => {
            if (snapshot.exists()) {
              const updatedData = {
                id: bandId,
                ...snapshot.data()
              };
              
              // Update both caches
              saveToCache(bandId, updatedData, BAND_CACHE_PREFIX);
              
              setEnrichedBandData(prev => ({
                ...prev,
                [bandId]: updatedData
              }));
              
              // Update user's associated acts and bands that reference this band
              setUserData(prev => {
                if (!prev) return prev;
                
                // Update associatedActs if they exist
                const updatedAssociatedActs = prev.associatedActs?.map(act => {
                  if (act.type === 'band' && act.id === bandId) {
                    return {
                      ...act,
                      name: updatedData.bandFullName || act.name,
                      username: updatedData.bandUserName || act.username, 
                      photoUrl: updatedData.bandProfilePictureUrl || act.photoUrl,
                      genre: updatedData.bandGenres?.join(', ') || act.genre,
                      verified: updatedData.verified || false
                    };
                  }
                  return act;
                });
                
                // Update bands if they exist
                const updatedBands = prev.bands?.map(band => {
                  if (band.id === bandId) {
                    return {
                      ...band,
                      name: updatedData.bandFullName || band.name,
                      username: updatedData.bandUserName || band.username,
                      photoUrl: updatedData.bandProfilePictureUrl || band.photoUrl,
                      genre: updatedData.bandGenres?.join(', ') || band.genre,
                      verified: updatedData.verified || false
                    };
                  }
                  return band;
                });
                
                // Save updated user data to cache
                const updatedUserData = {
                  ...prev,
                  associatedActs: updatedAssociatedActs || prev.associatedActs,
                  bands: updatedBands || prev.bands
                };
                
                saveToCache(prev.userId, updatedUserData);
                
                return updatedUserData;
              });
            }
          },
          error => {
            console.error(`Error in band listener for ${bandId}:`, error);
          }
        );
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching band data:', error);
      return null;
    }  
  };

 /**
 * Get artist data with smart caching
 */
const getArtistDataFromCache = async (artistId, forceRefresh = false) => {
  // Check if we already have this artist data in cache
  const cacheKey = `artist_${artistId}`;
  
  // First check localStorage cache if not forcing refresh
  if (!forceRefresh) {
    const cachedData = getFromCache(artistId, 'artistDataCache_');
    if (cachedData) return cachedData;
  }
  
  try {
    // Fetch from Firestore
    const db = getFirestoreInstance();
    const artistDoc = await getDoc(doc(db, 'users', artistId));
    if (!artistDoc.exists()) return null;
    
    const data = {
      id: artistId,
      ...artistDoc.data()
    };
    
    // Extract only the fields we need for display
    const artistData = {
      id: artistId,
      name: data.fullName || '',
      username: data.userName || '',
      photoUrl: data.profilePictureUrl || '',
      subType: data.userSubType || '',
      verified: data.idVerificationStatus === 'Verified' || false,
      location: [data.city, data.state, data.country].filter(Boolean).join(', '),
      type: 'artist'
    };
    
    // Save to cache
    saveToCache(artistId, artistData, 'artistDataCache_');
    
    return artistData;
  } catch (error) {
    console.error('Error fetching artist data:', error);
    return null;
  }
};

/**
 * Enriches minimal acts with full data
 */
const enrichActs = async (minimalActs, source) => {
  if (!minimalActs || minimalActs.length === 0) return [];
  
  // First deduplicate the array by ID and type
  const uniqueActs = [];
  const uniqueKeys = new Set();
  
  minimalActs.forEach(act => {
    const key = `${act.id}_${act.type || (source === 'bands' ? 'band' : 'artist')}`;
    if (!uniqueKeys.has(key)) {
      uniqueKeys.add(key);
      uniqueActs.push(act);
    }
  });
  
  return Promise.all(
    uniqueActs.map(async act => {
      // Ensure act has a type (for backward compatibility)
      const actType = act.type || (source === 'bands' ? 'band' : 'artist');
      
      // For bands, fetch and add full data
      if (actType === 'band') {
        const bandData = await getBandDataFromCache(act.id);
        if (bandData) {
          return {
            id: act.id,
            type: 'band', // Always ensure type is set properly
            name: bandData.bandFullName || '',
            username: bandData.bandUserName || '',
            photoUrl: bandData.bandProfilePictureUrl || '',
            subType: bandData.subType || '',
            genre: bandData.bandGenres?.join(', ') || '',
            verified: bandData.verified || false
          };
        }
      } 
      // For artists, fetch and add full data
      else if (actType === 'artist') {
        const artistData = await getArtistDataFromCache(act.id);
        if (artistData) {
          return {
            id: act.id,
            type: 'artist',
            name: artistData.name || '',
            username: artistData.username || '',
            photoUrl: artistData.photoUrl || '',
            subType: artistData.subType || '',
            verified: artistData.verified || false,
            location: artistData.location || ''
          };
        }
      }
      
      // If data fetching failed or for other types, return as is
      return {
        ...act,
        type: actType // Ensure type is always set
      };
    })
  );
};

// Add this new function to UserContext.js
const getProfileData = async (userId) => {
  // Similar to getUserDataFromCache but NEVER modifies context state
  const cachedData = getFromCache(userId);
  if (cachedData) return cachedData;
  
  try {
    const data = await getUserData(userId);
    
    // Process data as before
    if (data.associatedActs?.length) {
      data.associatedActs = await enrichActs(data.associatedActs, 'associatedActs');
    }
    
    if (data.bands?.length) {
      data.bands = await enrichActs(data.bands, 'bands');
    }
    
    // Save to cache only
    saveToCache(userId, data);
    
    // DON'T modify context - just return data
    return data;
  } catch (error) {
    console.error('Error fetching profile data:', error);
    throw error;
  }
};

  /**
   * Get user data with smart caching and listeners
   */
  const getUserDataFromCache = useCallback(async (userId) => {
    // Check user-specific cache first
    const cachedData = getFromCache('userData', CACHE_PREFIX, userId);
    
    if (cachedData) {
      setUserData(cachedData);
      setupUserListener(userId, cachedData);
      
      if (cachedData.associatedActs || cachedData.bands) {
        setupBandListeners(cachedData);
      }
      
      // Background enrichment with user-specific caching
      const enrichPromise = async () => {
        try {
          let needsUpdate = false;
          let updatedData = { ...cachedData };
          
          if (cachedData.associatedActs?.length) {
            const freshAssociatedActs = await enrichActs(cachedData.associatedActs, 'associatedActs');
            updatedData.associatedActs = freshAssociatedActs;
            needsUpdate = true;
          }
          
          if (cachedData.bands?.length) {
            const freshBands = await enrichActs(cachedData.bands, 'bands');
            updatedData.bands = freshBands;
            needsUpdate = true;
          }
          
          if (needsUpdate) {
            setUserData(updatedData);
            saveToCache('userData', updatedData, CACHE_PREFIX, userId);
          }
        } catch (error) {
          console.error('Error enriching cached data:', error);
        }
      };
      
      enrichPromise();
      return cachedData;
    }
    
    // If not in cache, fetch from Firestore
    try {
      const data = await getUserData(userId);
      
      // Enrich both associatedActs and bands with full data
      if (data.associatedActs?.length) {
        data.associatedActs = await enrichActs(data.associatedActs, 'associatedActs');
      }
      
      if (data.bands?.length) {
        data.bands = await enrichActs(data.bands, 'bands');
      }
      
      // Save to cache
      saveToCache(userId, data);
      
      // Set up listener for real-time updates
      setupUserListener(userId, data);
      
      // Set up band listeners
      if (data.associatedActs || data.bands) {
        setupBandListeners(data);
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  });

  /**
   * Get user's events (tours and shows) from assignedEvents subcollection
   */
  const getUserEvents = useCallback(async (userId) => {
    if (!userId) return [];
    
    try {
      const db = getFirestoreInstance();
      
      // Query the assignedEvents subcollection
      const assignedEventsRef = collection(db, 'users', userId, 'assignedEvents');
      const assignedEventsSnapshot = await getDocs(assignedEventsRef);
      
      if (!assignedEventsSnapshot.empty) {
        const eventsPromises = assignedEventsSnapshot.docs.map(async (doc) => {
          const eventData = doc.data();
          const eventType = eventData.type || 'tour';
          const eventId = eventType === 'tour' ? eventData.tourId : eventData.showId;
          
          if (!eventId) return null;
          
          // Get event details using the caching function
          return getEventDataFromCache(eventId, eventType);
        });
        
        const events = await Promise.all(eventsPromises);
        return events.filter(Boolean);
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching user events:', error);
      return [];
    }
  }, []);

  /**
   * Set up real-time listener for user document
   */
  const setupUserListener = (userId, initialData) => {
    // Clean up any existing user listener
    if (userListener.current) {
      userListener.current();
      userListener.current = null;
    }
    
    // Set up new listener
    const db = getFirestoreInstance();
    userListener.current = onSnapshot(
      doc(db, 'users', userId),
      async (snapshot) => {
        if (snapshot.exists()) {
          const firestoreData = snapshot.data();
          
          // Merge with existing data to preserve enriched fields
          const updatedData = {
            userId,
            ...firestoreData
          };
          
          // Update localStorage cache
          saveToCache(userId, updatedData);
          
          // Check if we need to update band listeners
          const hasNewBands = checkForNewBands(
            userData, 
            updatedData
          );
          
          // Enrich the data before updating state
          if (updatedData.associatedActs?.length) {
            updatedData.associatedActs = await enrichActs(
              updatedData.associatedActs, 
              'associatedActs'
            );
          }
          
          if (updatedData.bands?.length) {
            updatedData.bands = await enrichActs(
              updatedData.bands, 
              'bands'
            );
          }
          
          // Update React state
          setUserData(updatedData);
          
          // If there are new bands, update listeners
          if (hasNewBands) {
            setupBandListeners(updatedData);
          }
        }
      },
      error => {
        console.error(`Error in user listener for ${userId}:`, error);
      }
    );
  };

  /**
   * Check if new bands have been added or removed
   */
  const checkForNewBands = (oldData, newData) => {
    if (!oldData) return true;
    
    const oldBandIds = new Set();
    const newBandIds = new Set();
    
    // Collect old band IDs
    if (oldData.associatedActs) {
      oldData.associatedActs
        .filter(act => act.type === 'band')
        .forEach(act => oldBandIds.add(act.id));
    }
    
    if (oldData.bands) {
      oldData.bands.forEach(band => oldBandIds.add(band.id));
    }
    
    // Collect new band IDs
    if (newData.associatedActs) {
      newData.associatedActs
        .filter(act => act.type === 'band')
        .forEach(act => newBandIds.add(act.id));
    }
    
    if (newData.bands) {
      newData.bands.forEach(band => newBandIds.add(band.id));
    }
    
    // Check if there are any differences
    if (oldBandIds.size !== newBandIds.size) return true;
    
    for (const bandId of oldBandIds) {
      if (!newBandIds.has(bandId)) return true;
    }
    
    for (const bandId of newBandIds) {
      if (!oldBandIds.has(bandId)) return true;
    }
    
    return false;
  };

  /**
   * Set up listeners for all band data the user is connected to
   */
  const setupBandListeners = (userData) => {
    if (!userData) return;
    
    // Collect all unique band IDs from both arrays
    const bandIds = new Set();
    
    if (userData.associatedActs) {
      userData.associatedActs
        .filter(act => act.type === 'band')
        .forEach(act => bandIds.add(act.id));
    }
    
    if (userData.bands) {
      userData.bands.forEach(band => bandIds.add(band.id));
    }
    
    // Remove listeners for bands that are no longer referenced
    Object.keys(bandListeners.current).forEach(bandId => {
      if (!bandIds.has(bandId)) {
        if (typeof bandListeners.current[bandId] === 'function') {
          bandListeners.current[bandId]();
          delete bandListeners.current[bandId];
        }
      }
    });
    
    // Add listeners for new bands
    bandIds.forEach(async bandId => {
      if (!bandListeners.current[bandId]) {
        await getBandDataFromCache(bandId); // This will set up the listener
      }
    });
  };

  /**
   * Add, remove, or update a band membership
   */
  const updateBandMembership = async (bandId, action, role = 'member') => {
    if (!userData) return;
    
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
      
      if (action === 'remove') {
        // Create minimal versions to remove
        const bandToRemove = { id: bandId };
        
        // Remove from Firestore
        await updateDoc(userRef, {
          bands: arrayRemove(bandToRemove)
        });
        
        // Also check if it's in associatedActs and remove if found
        const associatedActToRemove = userData.associatedActs?.find(
          act => act.type === 'band' && act.id === bandId
        );
        
        if (associatedActToRemove) {
          const minimalActToRemove = { 
            id: bandId,
            type: 'band'
          };
          
          await updateDoc(userRef, {
            associatedActs: arrayRemove(minimalActToRemove)
          });
        }
        
        // User listener will handle state update
      } 
      else if (action === 'add') {
        // Fetch latest band data
        const bandData = await getBandDataFromCache(bandId, true);
        if (!bandData) throw new Error('Band data not found');
        
        // Create minimal version for Firestore
        const bandToAdd = { id: bandId, role };
        
        // Add to Firestore
        await updateDoc(userRef, {
          bands: arrayUnion(bandToAdd)
        });
        
        // If not already in associatedActs, add it there too
        const isInAssociatedActs = userData.associatedActs?.some(
          act => act.type === 'band' && act.id === bandId
        );
        
        if (!isInAssociatedActs) {
          const actToAdd = {
            id: bandId,
            type: 'band'
          };
          
          await updateDoc(userRef, {
            associatedActs: arrayUnion(actToAdd)
          });
        }
        
        // User listener will handle state update
      }
      else if (action === 'updateRole') {
        // Find current band to get its data
        const currentBand = userData.bands?.find(band => band.id === bandId);
        
        if (currentBand) {
          // Create minimal versions for remove and add
          const bandToRemove = { 
            id: bandId, 
            role: currentBand.role 
          };
          
          const bandToAdd = { 
            id: bandId, 
            role 
          };
          
          // Update Firestore
          await updateDoc(userRef, {
            bands: arrayRemove(bandToRemove)
          });
          
          await updateDoc(userRef, {
            bands: arrayUnion(bandToAdd)
          });
          
          // User listener will handle state update
        }
      }
    } catch (error) {
      console.error('Error updating band membership:', error);
      throw error;
    }
  };

  /**
   * Add an associated act with minimal data in Firestore
   */
  const addAssociatedAct = async (act) => {
    if (!userData || !act) return;
    
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
      
      // Ensure we have the correct type - explicitly check for 'band'
      const actType = act.type === 'band' ? 'band' : 'artist';
      
      // Create minimal version for storage with ONLY id and type
      const minimalAct = {
        id: act.id,
        type: actType
      };
      
      // Check if already in Firestore to prevent duplicates
      const isExisting = userData.associatedActs?.some(
        existingAct => existingAct.id === act.id && existingAct.type === actType
      );
      
      if (isExisting) {
        console.log('Act already exists, skipping Firestore update');
        return null; // Already exists, don't add again
      }
      
      // IMPORTANT: Only store minimal data in Firestore
      await updateDoc(userRef, {
        associatedActs: arrayUnion(minimalAct)
      });
      
      // Let the Firestore listener handle state updates
      // We don't need to manually update state here
      // The UserProvider's useEffect with onSnapshot will handle this
      
      return true; // Success
    } catch (error) {
      console.error('Error adding associated act:', error);
      throw error;
    }
  };

  /**
   * Remove an associated act
   */
  const removeAssociatedAct = async (actId, actType) => {
    if (!userData) return;
    
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
      
      // Create minimal version to remove
      const actToRemove = {
        id: actId,
        type: actType
      };
      
      // Update Firestore
      await updateDoc(userRef, {
        associatedActs: arrayRemove(actToRemove)
      });
      
      // User listener will handle state update
    } catch (error) {
      console.error('Error removing associated act:', error);
      throw error;
    }
  };

  /**
   * Manual refresh of all band data from Firestore
   * Useful when you need to force a refresh
   */
  const refreshBandData = async () => {
    if (!userData) return;
    
    try {
      // Collect all unique band IDs
      const bandIds = new Set();
      
      if (userData.associatedActs) {
        userData.associatedActs
          .filter(act => act.type === 'band')
          .forEach(act => bandIds.add(act.id));
      }
      
      if (userData.bands) {
        userData.bands.forEach(band => bandIds.add(band.id));
      }
      
      // Force refresh all band data
      await Promise.all(
        Array.from(bandIds).map(async bandId => {
          await getBandDataFromCache(bandId, true);
        })
      );
    } catch (error) {
      console.error('Error refreshing band data:', error);
    }
  };

  // Helper function to get bands where user is a member
  const getUserBands = () => {
    if (!userData || !userData.bands) return [];
    return userData.bands;
  };
  
  // Helper function to get pending band requests
  const getPendingBandRequests = () => {
    if (!userData || !userData.pendingActRequests) return [];
    return userData.pendingActRequests.filter(act => act.type === 'band');
  };

  // Set up user authentication and data fetching
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      if (user) {
        try {
          const token = await user.getIdToken();
          setAuthToken(token);
          
          // CRITICAL FIX: Save the token to localStorage
          localStorage.setItem('authToken', token);
          
          const userData = await getUserDataFromCache(user.uid);
          setUserData(userData);
          
          // Check and update membership status
          const updatedUserData = await checkAndUpdateMembershipStatus(user.uid, userData);
          if (updatedUserData !== userData) {
            setUserData(updatedUserData);
            saveToCache(user.uid, updatedUserData);
          }
        } catch (error) {
          console.error('Error:', error);
          setUserData(null);
          setAuthToken(null);
          // Clear from localStorage on error
          localStorage.removeItem('authToken');
        }
      } else {
        const { search, pathname } = window.location;
        const queryParams = queryString.parse(search);
        
        if (pathname === '/verify-email' && queryParams.oobCode) {
          try {
            const email = localStorage.getItem('emailForSignIn');
            if (email) {
              const result = await signInWithEmailLink(auth, email, window.location.href);
              if (result.user) {
                const token = await result.user.getIdToken();
                setAuthToken(token);
                
                // Save token to localStorage here too
                localStorage.setItem('authToken', token);
                
                const userData = await getUserDataFromCache(result.user.uid);
                setUserData(userData);
                
                localStorage.removeItem('emailForSignIn');
              }
            }
          } catch (error) {
            console.error('Error verifying email:', error);
          }
        } else if (pathname === '/reset-password' && queryParams.oobCode) {
          // Let ResetPassword component handle the token
        } else {
          clearCache();
          setUserData(null);
          setAuthToken(null);
          // Clear from localStorage when signing out
          localStorage.removeItem('authToken');
        }
      }
      setIsLoading(false);
    });
  
    return () => {
      unsubscribe();
      
      // Clean up listeners
      if (userListener.current) {
        userListener.current();
      }
      
      Object.values(bandListeners.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });

      // Clean up event listeners
      Object.values(eventListeners.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, []);

  
  const getRecommendationStatus = async (recommenderId, profileId) => {
    // Create a unique cache key for this recommendation relationship
    const cacheKey = `${recommenderId}_${profileId}`;
    
    // First check the cache
    const cachedStatus = getFromCache(cacheKey, RECOMMENDATION_CACHE_PREFIX);
    if (cachedStatus !== null) {
      return cachedStatus;
    }
    
    // If not in cache, query Firestore
    try {
      const db = getFirestoreInstance();
      const recsRef = collection(db, 'users', profileId, 'recommendations');
      const recsSnapshot = await getDocs(recsRef);
      
      let hasRecommended = false;
      
      // Check if the recommender has already recommended this profile
      for (const docSnapshot of recsSnapshot.docs) {
        const data = docSnapshot.data();
        if (data.recommendedBy && data.recommendedBy.includes(recommenderId)) {
          hasRecommended = true;
          break;
        }
      }
      
      // Save to cache
      saveToCache(cacheKey, hasRecommended, RECOMMENDATION_CACHE_PREFIX);
      
      return hasRecommended;
    } catch (error) {
      console.error('Error checking recommendation status:', error);
      return false;
    }
  };
  
  // Function to update recommendation cache status
  const updateRecommendationCache = (recommenderId, profileId, status) => {
    const cacheKey = `${recommenderId}_${profileId}`;
    saveToCache(cacheKey, status, RECOMMENDATION_CACHE_PREFIX);
  };
  

  // Export the context value with the same function names as before
  return (
    <UserContext.Provider 
      value={{
        userData,
        setUserData,
        authToken,
        getUserDataFromCache,
        getBandDataFromCache,
        getArtistDataFromCache, 
        getProfileData,
        getEventDataFromCache, // New function to get event data
        getUserEvents, // New function to get user's events
        updateUserData: async (data) => {
          try {
            await updateUserProfile(data.userId, data);
            // The listener will update the state
          } catch (error) {
            console.error('Error updating user data:', error);
            throw error;
          }
        },
        updateBandMembership,
        getUserBands,
        getPendingBandRequests,
        refreshBandData,
        addAssociatedAct,
        removeAssociatedAct,
        enrichedBandData,
        clearCache,
        isLoading,
        getRecommendationStatus,
        updateRecommendationCache
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;